import {
  Object3D,
  Plane,
  Quaternion,
  Raycaster,
  Vector2,
  Vector3,
} from "three";
import { ICameraBehavior } from "../../utils/camera-utils";
import {
  GenericOrbitRotateControl,
  IOrbitRotateArguments,
} from "../controls/rotate-controls/generic-orbit-rotate-control";
import {
  IPerspectiveFixedPointZoomArguments,
  PerspectiveFixedPointZoomControl,
} from "../controls/zoom-controls/perspective-fixed-point-zoom-control";
import { Quick3DPerspectiveCamera } from "../quick3d-perspective-camera";

export class PerspectiveOrbitBehavior implements ICameraBehavior {
  private camera: Quick3DPerspectiveCamera;
  private _deltaNDC: Vector2;
  private rotateControl: GenericOrbitRotateControl;
  private rotateControlArguments: IOrbitRotateArguments;
  private targetPoint: Object3D;
  private worldOrientation: Quaternion;
  private zoomControl: PerspectiveFixedPointZoomControl;
  private zoomControlArguments: IPerspectiveFixedPointZoomArguments;

  private panPlane: Plane;
  private rayCaster: Raycaster;

  public panSpeed = 0;

  public get rotateSpeed(): number {
    return this.rotateControlArguments.rotateSpeed as number;
  }
  // public get panSpeed(): number { return 0; }
  public get zoomSpeed(): number {
    return this.zoomControlArguments.zoomStep as number;
  }

  // public set panSpeed(_val: number) { }
  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  public set rotateSpeed(val: number) {
    this.rotateControlArguments.rotateSpeed = val;
  }
  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  public set zoomSpeed(val: number) {
    this.zoomControlArguments.zoomStep = val;
  }

  public setDeltaNDC(client: Vector2) {
    this._deltaNDC = client;
  }
  public alternativeWheel: boolean;

  constructor(
    camera: Quick3DPerspectiveCamera,
    targetPoint: Object3D,
    worldOrientation: Quaternion
  ) {
    this.camera = camera;
    this.targetPoint = targetPoint;
    this._deltaNDC = new Vector2();
    this.rotateControl = new GenericOrbitRotateControl();
    this.rotateControlArguments = {
      target: new Vector3(),
    } as IOrbitRotateArguments;
    this.worldOrientation = worldOrientation;
    this.zoomControl = new PerspectiveFixedPointZoomControl();
    this.zoomControlArguments = { zoomStep: 5 };
    this.panPlane = new Plane();
    this.rayCaster = new Raycaster();
  }

  public start(
    ndcX: number,
    ndcY: number
    // _button?: PointerEventButtonIDs,
    // ifcProject?: IfcProjectManager,
    // ifcRenderer?: IFCRenderer
  ): void {
    this._deltaNDC.set(ndcX, ndcY);
    this.rayCaster.setFromCamera(new Vector3(ndcX, ndcY, 0), this.camera);
    const pointOnIfcModel = new Vector3();
    // if (ifcProject) {
    //     const plane = ifcRenderer?.renderer.clippingPlanes[0] as Plane;
    //     if (plane && plane.distanceToPoint(this.rayCaster.ray.origin) < 0) {
    //         const newOrigin = new Vector3();
    //         this.rayCaster.ray.intersectPlane(plane, newOrigin);
    //         this.rayCaster.ray.origin.copy(newOrigin);
    //     }
    //     if (ifcProject.getClosestPoint(this.rayCaster, pointOnIfcModel)) {
    //         this.panPlane.setFromNormalAndCoplanarPoint(
    //             this.camera.getWorldDirection(new Vector3()),
    //             pointOnIfcModel
    //         );
    //     } else {
    //         this.panPlane.setFromNormalAndCoplanarPoint(
    //             this.camera.getWorldDirection(new Vector3()),
    //             ifcProject.boundingBox.getCenter(new Vector3())
    //         );
    //     }
    // } else {
    this.panPlane.setFromNormalAndCoplanarPoint(
      this.camera.getWorldDirection(new Vector3()),
      new Vector3()
    );
    // }
  }

  public rotate(ndcX: number, ndcY: number): void {
    this.rotateControlArguments.rotateSpeed = 5 / 3;
    this.rotateControlArguments.horizontalAngle = this._deltaNDC.x - ndcX;
    this.rotateControlArguments.verticalAngle = this._deltaNDC.y - ndcY;
    this.rotateControlArguments.target?.copy(this.targetPoint.position);

    this.targetPoint.applyQuaternion(this.worldOrientation.invert());
    this.rotateControl.rotate(this.targetPoint, this.rotateControlArguments);
    this.targetPoint.applyQuaternion(this.worldOrientation.invert());
    this.targetPoint.updateMatrixWorld(true);
    this._deltaNDC.set(ndcX, ndcY);
  }

  public setRotateCenter(ndcX: number, ndcY: number): Vector3 {
    return;
  }

  public pan(ndcX: number, ndcY: number): void {
    this.rayCaster.setFromCamera(new Vector2(ndcX, ndcY), this.camera);
    const newPointUnderMouse = new Vector3();
    this.rayCaster.ray.intersectPlane(this.panPlane, newPointUnderMouse);

    this.rayCaster.setFromCamera(
      new Vector2(this._deltaNDC.x, this._deltaNDC.y),
      this.camera
    );
    const pointUnderMouse = new Vector3();
    this.rayCaster.ray.intersectPlane(this.panPlane, pointUnderMouse);

    const positionDelta = new Vector3().subVectors(
      pointUnderMouse,
      newPointUnderMouse
    );
    positionDelta.applyQuaternion(
      this.camera.getWorldQuaternion(new Quaternion()).invert()
    );
    this.camera.position.add(positionDelta);
    this.camera.updateMatrix();
    this._deltaNDC.set(ndcX, ndcY);
  }

  public zoom(delta: number): void {
    this.zoomControlArguments.zoomDirection = ((delta / Math.abs(delta)) *
      -1) as 1 | -1;
    this.zoomControlArguments.ndcPointX = this._deltaNDC.x;
    this.zoomControlArguments.ndcPointY = this._deltaNDC.y;
    this.zoomControl.zoom(this.camera, this.zoomControlArguments);
  }

  public getZoomDirection(ndcX: number, ndcY: number): number {
    return this._deltaNDC.y - ndcY < 0 ? 1 : -1;
  }

  public wheel(ndcX: number, ndcY: number, wheelDirection: 1 | -1): void {
    this.zoomControlArguments.zoomDirection = (wheelDirection * -1) as 1 | -1;
    this.zoomControlArguments.ndcPointX = ndcX;
    this.zoomControlArguments.ndcPointY = ndcY;
    this.zoomControl.zoom(this.camera, this.zoomControlArguments);
    this._deltaNDC.set(ndcX, ndcY);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public end(): void {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public update(): void {}

  public flatZoom(delta: 1 | -1): void {
    this.wheel(0, 0, delta);
  }

  public flatPan(ndcDeltaX: number, ndcDeltaY: number): void {
    this.pan(this._deltaNDC.x + ndcDeltaX, this._deltaNDC.y + ndcDeltaY);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public flatRotate(): void {}
}
